import axios, { AxiosInstance } from 'axios';
import { store } from '../../store';
import {
  authClearAction,
  postLoginSuccessApiAction,
} from '../../modules/auth/store/reducer';
import { courseClearAction } from '../../modules/course/store/reducer';
import { meClearAction } from '../../modules/me/store/reducer';
import { classClearAction } from '../../modules/class/store/reducer';
import { globalClearAction } from '../../modules/global/store/reducer';
import { userClearAction } from '../../modules/user/store/reducer';
import { subscriptionClearAction } from '../../modules/subscription/store/reducer';
import { templateClearAction } from '../../modules/template/store/reducer';
import { reportClearAction } from '../../modules/report/store/reducer';
import { notificationClearAction } from '../../modules/notification/store/reducer';
import { PostRefreshResponse } from '../../modules/auth/interfaces/post-refresh';

const API_URL =
  process.env.REACT_APP_API_URL || 'https://api.academia-inversapiens.com/api';

let refreshPromise: Promise<string> | null = null;

const clearSession = () => {
  store.dispatch(authClearAction());
  store.dispatch(classClearAction());
  store.dispatch(courseClearAction());
  store.dispatch(globalClearAction());
  store.dispatch(meClearAction());
  store.dispatch(userClearAction());
  store.dispatch(subscriptionClearAction());
  store.dispatch(templateClearAction());
  store.dispatch(reportClearAction());
  store.dispatch(notificationClearAction());
};

const refreshAccessToken = async (): Promise<string> => {
  const { refreshToken } = store.getState().auth;
  const { user } = store.getState().me;

  if (!refreshToken || !user?.email) {
    throw new Error('Missing refresh credentials');
  }

  // Bare axios call — avoids recursive interceptor on auth/refresh
  const { data } = await axios.post<PostRefreshResponse>(`${API_URL}/auth/refresh`, {
    email: user.email,
    refreshToken,
  });

  store.dispatch(postLoginSuccessApiAction(data));
  return data.token;
};

const isAuthUrl = (url = '') =>
  url.includes('auth/login') ||
  url.includes('auth/google') ||
  url.includes('auth/refresh') ||
  url.includes('auth/signup');

export const refreshTokenInterceptor = (instance: AxiosInstance) =>
  async function (error: any) {
    const originalRequest = error?.config;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    const status = error?.response?.status;
    if (status !== 401 || isAuthUrl(originalRequest.url) || originalRequest._retry) {
      if (status === 401 && originalRequest._retry) {
        clearSession();
      }
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }

      const token = await refreshPromise;

      originalRequest.headers = {
        ...originalRequest.headers,
        Authorization: `Bearer ${token}`,
      };

      return instance(originalRequest);
    } catch (e) {
      console.error('Error Refreshing Token', e);
      clearSession();
      return Promise.reject(error);
    }
  };
